import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { Logo } from '@/design-system/Logo';
import { Button } from '@/design-system/Button';
import { cn } from '@/design-system/cn';
import { BRAND } from '@/contracts/brand';

const NAV = [
  { to: '/how-it-works', label: 'How it works' },
  { to: '/stories', label: 'Stories' },
  { to: '/for-parents', label: 'For parents' },
  { to: '/pricing', label: 'Pricing' },
];

const FOOTER_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/community-guidelines', label: 'Community guidelines' },
  { to: '/accessibility', label: 'Accessibility' },
  { to: '/help', label: 'Help' },
  { to: '/contact', label: 'Contact' },
];

/**
 * Public website shell (spec §2.2).
 *
 * Deliberately a separate layout from the family app: different navigation,
 * different data policy, and no dependency on a signed-in session — the
 * marketing site must load for a signed-out visitor (spec §19.1 flagged the
 * old build for putting the public home underneath a global auth component).
 */
export function MarketingLayout() {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-paper">
      <a href="#main" className="u-sr focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-pebble focus:bg-card focus:px-l focus:py-m">
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b-2 border-line/60 bg-paper/85 backdrop-blur-md">
        <div className="u-shell flex items-center justify-between gap-l py-m">
          <Link to="/" aria-label={`${BRAND.spokenName} home`}>
            <Logo size="sm" />
          </Link>

          <nav className="hidden items-center gap-xs lg:flex" aria-label="Main">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  cn(
                    'rounded-pebble px-m py-s font-display text-[15px] font-semibold transition',
                    isActive ? 'bg-primary-soft text-primary-deep' : 'text-ink-soft hover:bg-paper-deep',
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-s">
            <Link
              to="/app"
              className="hidden rounded-pebble px-m py-s font-display text-[15px] font-semibold text-ink-soft hover:bg-paper-deep sm:block"
            >
              Sign in
            </Link>
            <Button variant="magic" size="sm" to="/try">
              Try a story
            </Button>
            <button
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label="Menu"
              className="flex h-touch w-touch items-center justify-center rounded-pebble border-2 border-line lg:hidden"
            >
              <span aria-hidden className="relative block h-[2px] w-5 bg-ink before:absolute before:-top-[6px] before:block before:h-[2px] before:w-5 before:bg-ink before:content-[''] after:absolute after:top-[6px] after:block after:h-[2px] after:w-5 after:bg-ink after:content-['']" />
            </button>
          </div>
        </div>

        {open ? (
          <nav className="u-shell grid gap-xs pb-l lg:hidden" aria-label="Main">
            {[...NAV, { to: '/app', label: 'Sign in' }].map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded-pebble px-m py-m font-display text-[16px] font-semibold text-ink-soft hover:bg-paper-deep"
              >
                {n.label}
              </Link>
            ))}
          </nav>
        ) : null}
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t-2 border-line bg-paper-deep/50">
        <div className="u-shell py-2xl">
          <div className="flex flex-col gap-xl sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-xs">
              <Logo size="sm" />
              <p className="mt-m text-[15px] leading-relaxed text-ink-soft">{BRAND.tagline}</p>
              {/* An App Store badge appears only when a real listing exists
                  (spec §5 W01, §17.4). */}
              {BRAND.appStoreUrl ? (
                <a href={BRAND.appStoreUrl} className="mt-l inline-block font-display text-[15px] font-semibold text-primary">
                  Download on the App Store
                </a>
              ) : (
                <p className="mt-l text-[14px] text-ink-faint">
                  The iPhone and iPad app is in development. There is no App Store listing yet.
                </p>
              )}
            </div>

            <nav aria-label="Footer" className="grid grid-cols-2 gap-x-2xl gap-y-s">
              {FOOTER_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="text-[15px] font-semibold text-ink-soft hover:text-ink">
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

          <p className="u-hairline mt-2xl pt-l text-[14px] text-ink-faint">
            {BRAND.publisher ?? 'Publisher details to be confirmed before launch.'} · Story
            illustrations and characters are original work created for this product.
          </p>
        </div>
      </footer>
    </div>
  );
}
